import { useState, useEffect, useRef } from "react";
import { api, API, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { DollarSign, FileText, Save, Camera, User, MessageSquare, Star } from "lucide-react";

export default function SiteConfiguration() {
  const [activeSection, setActiveSection] = useState("plans");
  const [plans, setPlans] = useState([]);
  const [homeContent, setHomeContent] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [newTestimonial, setNewTestimonial] = useState({ client_name: "", rating: 5, text: "" });
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadingCoachPhoto, setUploadingCoachPhoto] = useState(false);
  const [photoTimestamp, setPhotoTimestamp] = useState(Date.now());
  const coachPhotoInputRef = useRef(null);

  useEffect(() => {
    loadConfig();
    loadTestimonials();
  }, []);

  const loadConfig = async () => {
    try {
      const { data } = await api.get("/admin/config");
      setPlans(data.plans || []);
      setHomeContent(data.home_content || {});
    } catch (e) {
      toast.error("Erreur lors du chargement de la configuration");
    } finally {
      setLoading(false);
    }
  };

  const loadTestimonials = async () => {
    try {
      const { data } = await api.get("/admin/testimonials");
      setTestimonials(data || []);
    } catch (e) {
      console.error("Erreur lors du chargement des témoignages", e);
    }
  };

  const savePlans = async () => {
    setSaving(true);
    try {
      // Clean up plans before saving: remove empty features
      const cleanedPlans = plans.map(plan => ({
        ...plan,
        features: (plan.features || []).filter(f => f && f.trim() !== '')
      }));
      
      console.log("Sending plans:", cleanedPlans);
      await api.put("/admin/config/plans", cleanedPlans);
      toast.success("Forfaits mis à jour avec succès");
    } catch (e) {
      console.error("Save error:", e.response?.data);
      const errorDetail = e.response?.data?.detail;
      let errorMessage = "Erreur lors de la sauvegarde.";
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // Format Pydantic validation errors
        errorMessage = errorDetail.map(err => 
          `${err.loc?.join('.') || 'Champ'}: ${err.msg}`
        ).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const saveHomeContent = async () => {
    setSaving(true);
    try {
      await api.put("/admin/config/home", homeContent);
      toast.success("Contenu de la page d'accueil mis à jour");
    } catch (e) {
      const errorMessage = typeof e.response?.data?.detail === 'string' 
        ? e.response.data.detail 
        : e.response?.data?.detail?.[0]?.msg 
        || "Erreur lors de la sauvegarde. Vérifiez les champs.";
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const updatePlan = (index, field, value) => {
    const newPlans = [...plans];
    newPlans[index] = { ...newPlans[index], [field]: value };
    setPlans(newPlans);
  };

  const updateWhyItem = (index, field, value) => {
    const newItems = [...(homeContent.why_items || [])];
    newItems[index] = { ...newItems[index], [field]: value };
    setHomeContent({ ...homeContent, why_items: newItems });
  };

  const handleCoachPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Le fichier doit être une image");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5 MB");
      return;
    }

    setUploadingCoachPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await api.post("/admin/config/upload-coach-photo", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Photo du coach mise à jour");
      // Reload config to get updated photo URL
      await loadConfig();
      // Force image refresh
      setPhotoTimestamp(Date.now());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploadingCoachPhoto(false);
      if (coachPhotoInputRef.current) {
        coachPhotoInputRef.current.value = "";
      }
    }
  };

  const addTestimonial = async () => {
    if (!newTestimonial.client_name || !newTestimonial.text) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.post("/admin/testimonials", newTestimonial);
      setTestimonials([...testimonials, data]);
      setNewTestimonial({ client_name: "", rating: 5, text: "" });
      toast.success("Témoignage ajouté avec succès");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'ajout");
    } finally {
      setSaving(false);
    }
  };

  const updateTestimonial = async (id) => {
    if (!editingTestimonial) return;

    setSaving(true);
    try {
      const { data } = await api.put(`/admin/testimonials/${id}`, editingTestimonial);
      setTestimonials(testimonials.map(t => t.id === id ? data : t));
      setEditingTestimonial(null);
      toast.success("Témoignage mis à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  const deleteTestimonial = async (id) => {
    if (!confirm("Supprimer ce témoignage ?")) return;

    try {
      await api.delete(`/admin/testimonials/${id}`);
      setTestimonials(testimonials.filter(t => t.id !== id));
      toast.success("Témoignage supprimé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-stone-400">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl">
      {/* Section Tabs */}
      <div className="mb-8 flex gap-2 border-b border-stone-700">
        <button
          onClick={() => setActiveSection("plans")}
          className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
            activeSection === "plans"
              ? "border-stone-100 text-stone-100"
              : "border-transparent text-stone-400 hover:text-stone-200"
          }`}
        >
          <DollarSign className="h-4 w-4" />
          Forfaits & Tarifs
        </button>
        <button
          onClick={() => setActiveSection("home")}
          className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
            activeSection === "home"
              ? "border-stone-100 text-stone-100"
              : "border-transparent text-stone-400 hover:text-stone-200"
          }`}
        >
          <FileText className="h-4 w-4" />
          Contenu Page d&apos;Accueil
        </button>
        <button
          onClick={() => setActiveSection("coach")}
          className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
            activeSection === "coach"
              ? "border-stone-100 text-stone-100"
              : "border-transparent text-stone-400 hover:text-stone-200"
          }`}
        >
          <User className="h-4 w-4" />
          Équipe / Coach
        </button>
        <button
          onClick={() => setActiveSection("testimonials")}
          className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
            activeSection === "testimonials"
              ? "border-stone-100 text-stone-100"
              : "border-transparent text-stone-400 hover:text-stone-200"
          }`}
        >
          <MessageSquare className="h-4 w-4" />
          Témoignages
        </button>
      </div>

      {/* Plans Section */}
      {activeSection === "plans" && (
        <div>
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-light text-stone-100">Gestion des Forfaits</h2>
              <p className="mt-1 text-sm text-stone-400">
                Modifiez les prix, noms et descriptions. Les forfaits inactifs sont masqués du site.
              </p>
            </div>
            <Button onClick={savePlans} disabled={saving} className="rounded-full bg-white px-6 text-stone-900 hover:bg-stone-100">
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>

          <div className="space-y-4">
            {plans.map((plan, index) => (
              <div key={plan.lookup_key} className="rounded-2xl border border-blue-500/30 bg-black p-6">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="rounded-full bg-stone-800 px-3 py-1 text-xs font-medium text-stone-300">
                      {plan.type === "coaching" ? "Coaching" : plan.type === "libre" ? "Accès Libre" : "Individuel"}
                    </span>
                    <Label className="text-xs text-stone-400">Actif</Label>
                    <Switch
                      checked={plan.active}
                      onCheckedChange={(v) => updatePlan(index, "active", v)}
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Nom du forfait</Label>
                    <Input
                      value={plan.name}
                      onChange={(e) => updatePlan(index, "name", e.target.value)}
                      className="mt-2 h-11 bg-white text-stone-900"
                      placeholder="Ex: Coaching · 2 séances/sem"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Prix (€)</Label>
                      <Input
                        type="number"
                        value={plan.price}
                        onChange={(e) => updatePlan(index, "price", parseInt(e.target.value) || 0)}
                        className="mt-2 h-11 bg-white text-stone-900"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Séances/sem</Label>
                      <Input
                        type="number"
                        value={plan.sessions_per_week}
                        onChange={(e) => updatePlan(index, "sessions_per_week", parseInt(e.target.value) || 0)}
                        className="mt-2 h-11 bg-white text-stone-900"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Description</Label>
                    <Textarea
                      value={plan.description || ""}
                      onChange={(e) => updatePlan(index, "description", e.target.value)}
                      className="mt-2 min-h-[80px] bg-white text-stone-900"
                      placeholder="Décrivez ce forfait..."
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">
                      Texte promo (optionnel)
                    </Label>
                    <Input
                      value={plan.promo_text || ""}
                      onChange={(e) => updatePlan(index, "promo_text", e.target.value)}
                      className="mt-2 h-11 bg-white text-stone-900"
                      placeholder="Ex: ⭐ Recommandé, -20% premier mois"
                    />
                  </div>
                </div>

                {/* Features Section */}
                <div className="mt-6 rounded-lg border border-blue-500/20 bg-stone-950 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-[0.2em] text-stone-300">
                      Caractéristiques ({(plan.features || []).length})
                    </Label>
                    <Button
                      type="button"
                      onClick={() => {
                        const updated = [...plans];
                        updated[index].features = [...(updated[index].features || []), ""];
                        setPlans(updated);
                      }}
                      className="h-7 rounded-full bg-blue-500 px-3 text-xs text-white hover:bg-blue-600"
                    >
                      + Ajouter
                    </Button>
                  </div>
                  <p className="mb-3 text-xs text-stone-500">
                    Les 3 lignes affichées sous le prix sur la carte du forfait
                  </p>
                  <div className="space-y-2">
                    {(plan.features || []).map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center gap-2">
                        <span className="flex h-6 w-6 items-center justify-center rounded bg-blue-500/20 text-xs text-blue-300">
                          {featureIndex + 1}
                        </span>
                        <Input
                          value={feature}
                          onChange={(e) => {
                            const updated = [...plans];
                            updated[index].features[featureIndex] = e.target.value;
                            setPlans(updated);
                          }}
                          className="h-9 flex-1 bg-white text-sm text-stone-900"
                          placeholder="Ex: Programme personnalisé"
                        />
                        <Button
                          type="button"
                          onClick={() => {
                            const updated = [...plans];
                            updated[index].features = updated[index].features.filter((_, i) => i !== featureIndex);
                            setPlans(updated);
                          }}
                          className="h-9 w-9 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30"
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                    {(!plan.features || plan.features.length === 0) && (
                      <p className="py-2 text-center text-xs text-stone-400">
                        Aucune caractéristique. Cliquez sur "+ Ajouter" pour en créer.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Home Content Section */}
      {activeSection === "home" && homeContent && (
        <div>
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-light text-stone-100">Contenu Page d&apos;Accueil</h2>
              <p className="mt-1 text-sm text-stone-400">
                Personnalisez tous les textes de votre page d&apos;accueil.
              </p>
            </div>
            <Button onClick={saveHomeContent} disabled={saving} className="rounded-full bg-white px-6 text-stone-900 hover:bg-stone-100">
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>

          <div className="space-y-6">
            {/* Hero Section */}
            <div className="rounded-2xl border border-blue-500/30 bg-black p-6">
              <h3 className="mb-4 text-lg font-medium text-stone-100">Section Hero (En-tête)</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Titre principal</Label>
                  <Textarea
                    value={homeContent.hero_title}
                    onChange={(e) => setHomeContent({ ...homeContent, hero_title: e.target.value })}
                    className="mt-2 min-h-[80px] bg-white text-lg text-stone-900"
                    placeholder="Un suivi adaptatif et une communauté soudée."
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Sous-titre</Label>
                  <Textarea
                    value={homeContent.hero_subtitle}
                    onChange={(e) => setHomeContent({ ...homeContent, hero_subtitle: e.target.value })}
                    className="mt-2 min-h-[100px] bg-white text-stone-900"
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Bouton principal</Label>
                    <Input
                      value={homeContent.hero_cta_primary}
                      onChange={(e) => setHomeContent({ ...homeContent, hero_cta_primary: e.target.value })}
                      className="mt-2 h-11 bg-white text-stone-900"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Bouton secondaire</Label>
                    <Input
                      value={homeContent.hero_cta_secondary}
                      onChange={(e) => setHomeContent({ ...homeContent, hero_cta_secondary: e.target.value })}
                      className="mt-2 h-11 bg-white text-stone-900"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Why Section */}
            <div className="rounded-2xl border border-blue-500/30 bg-black p-6">
              <h3 className="mb-4 text-lg font-medium text-stone-100">Section &quot;Pourquoi nous choisir&quot;</h3>
              <div className="mb-4">
                <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Titre de la section</Label>
                <Input
                  value={homeContent.why_title}
                  onChange={(e) => setHomeContent({ ...homeContent, why_title: e.target.value })}
                  className="mt-2 h-11 bg-white text-stone-900"
                />
              </div>

              <div className="space-y-4">
                {(homeContent.why_items || []).map((item, index) => (
                  <div key={index} className="rounded-lg border border-blue-500/20 bg-black p-4">
                    <div className="mb-3 text-xs font-medium uppercase tracking-wider text-stone-400">
                      Point {index + 1}
                    </div>
                    <div className="grid gap-3">
                      <Input
                        value={item.title}
                        onChange={(e) => updateWhyItem(index, "title", e.target.value)}
                        className="h-10 bg-white text-stone-900"
                        placeholder="Titre"
                      />
                      <Textarea
                        value={item.text || item.description || ""}
                        onChange={(e) => updateWhyItem(index, "text", e.target.value)}
                        className="min-h-[60px] bg-white text-sm text-stone-900"
                        placeholder="Description"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* About Section */}
            <div className="rounded-2xl border border-blue-500/30 bg-black p-6">
              <h3 className="mb-4 text-lg font-medium text-stone-100">Section À Propos / Coach</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Titre</Label>
                  <Input
                    value={homeContent.about_title}
                    onChange={(e) => setHomeContent({ ...homeContent, about_title: e.target.value })}
                    className="mt-2 h-11 bg-white text-stone-900 placeholder:text-stone-400"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Description</Label>
                  <Textarea
                    value={homeContent.about_description}
                    onChange={(e) => setHomeContent({ ...homeContent, about_description: e.target.value })}
                    className="mt-2 min-h-[120px] bg-white text-stone-900 placeholder:text-stone-400"
                  />
                </div>
              </div>
            </div>

            {/* Gallery Section */}
            <div className="rounded-2xl border border-blue-500/30 bg-black p-6">
              <h3 className="mb-4 text-lg font-medium text-stone-100">Galerie Photos</h3>
              <p className="mb-4 text-xs text-stone-400">
                Personnalisez les textes de la section galerie. Les photos se gèrent depuis l&apos;onglet Vue d&apos;ensemble.
              </p>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Texte au-dessus (eyebrow)</Label>
                  <Input
                    value={homeContent.gallery_eyebrow || ""}
                    onChange={(e) => setHomeContent({ ...homeContent, gallery_eyebrow: e.target.value })}
                    className="mt-2 h-11 bg-white text-stone-900 placeholder:text-stone-400"
                    placeholder="Galerie"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Titre principal</Label>
                  <Input
                    value={homeContent.gallery_title || ""}
                    onChange={(e) => setHomeContent({ ...homeContent, gallery_title: e.target.value })}
                    className="mt-2 h-11 bg-white text-stone-900 placeholder:text-stone-400"
                    placeholder="Le studio, au quotidien."
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Texte descriptif</Label>
                  <Textarea
                    value={homeContent.gallery_description || ""}
                    onChange={(e) => setHomeContent({ ...homeContent, gallery_description: e.target.value })}
                    className="mt-2 min-h-[80px] bg-white text-stone-900 placeholder:text-stone-400"
                    placeholder="Un espace pensé pour la performance, la récupération et les rencontres."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Coach Section */}
      {activeSection === "coach" && homeContent && (
        <div>
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-light text-stone-100">Équipe / Coach</h2>
              <p className="mt-1 text-sm text-stone-400">
                Gérez la photo et la présentation du coach sur la page d&apos;accueil.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Coach Photo */}
            <div className="rounded-2xl border border-blue-500/30 bg-black p-6">
              <h3 className="mb-4 text-lg font-medium text-stone-100">Photo du Coach</h3>
              
              <div className="flex items-center gap-6">
                {homeContent.coach_photo_url ? (
                  <div className="h-32 w-32 overflow-hidden rounded-full border-2 border-blue-500/30">
                    <img
                      src={`${BACKEND_URL}${homeContent.coach_photo_url}?t=${photoTimestamp}`}
                      alt="Coach"
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        console.error("Erreur chargement image:", e.target.src);
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex h-32 w-32 items-center justify-center rounded-full border-2 border-dashed border-stone-600 bg-stone-900">
                    <Camera className="h-8 w-8 text-stone-400" />
                  </div>
                )}
                
                <div className="flex-1">
                  <p className="mb-3 text-sm text-stone-400">
                    JPG, PNG ou GIF. Maximum 5 MB.
                  </p>
                  <input
                    ref={coachPhotoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleCoachPhotoUpload}
                    className="hidden"
                  />
                  <Button
                    onClick={() => coachPhotoInputRef.current?.click()}
                    disabled={uploadingCoachPhoto}
                    className="rounded-full bg-white px-6 text-stone-900 hover:bg-stone-100"
                  >
                    {uploadingCoachPhoto ? (
                      "Téléchargement..."
                    ) : (
                      <>
                        <Camera className="mr-2 h-4 w-4" />
                        {homeContent.coach_photo_url ? "Changer la photo" : "Ajouter une photo"}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Coach Info */}
            <div className="rounded-2xl border border-blue-500/30 bg-black p-6">
              <h3 className="mb-4 text-lg font-medium text-stone-100">Présentation du Coach</h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Titre de la section</Label>
                  <Input
                    value={homeContent.about_title}
                    onChange={(e) => setHomeContent({ ...homeContent, about_title: e.target.value })}
                    className="mt-2 h-11 bg-white text-stone-900"
                    placeholder="Ex: Ton coach"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Présentation</Label>
                  <Textarea
                    value={homeContent.about_description}
                    onChange={(e) => setHomeContent({ ...homeContent, about_description: e.target.value })}
                    className="mt-2 min-h-[150px] bg-white text-stone-900"
                    placeholder="Ton parcours, ta vision, ton approche..."
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">
                    Lien Instagram (optionnel)
                  </Label>
                  <Input
                    value={homeContent.coach_instagram_url || ""}
                    onChange={(e) => setHomeContent({ ...homeContent, coach_instagram_url: e.target.value })}
                    className="mt-2 h-11 bg-white text-stone-900"
                    placeholder="https://instagram.com/votreprofil"
                  />
                  <p className="mt-1 text-xs text-stone-400">
                    Apparaîtra comme bouton Instagram sur la page d&apos;accueil
                  </p>
                </div>
              </div>

              <Button
                onClick={saveHomeContent}
                disabled={saving}
                className="mt-6 w-full rounded-full bg-white py-5 text-stone-900 hover:bg-stone-100"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? "Enregistrement..." : "Enregistrer les modifications"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Testimonials Section */}
      {activeSection === "testimonials" && (
        <div className="rounded-xl border border-stone-800 bg-black p-6">
          <div className="mb-6">
            <h3 className="text-lg font-medium text-white">Gestion des Témoignages</h3>
            <p className="mt-1 text-sm text-stone-400">
              Ajoutez, modifiez ou supprimez les avis clients affichés sur votre page d&apos;accueil.
            </p>
          </div>

          {/* Add New Testimonial */}
          <div className="mb-8 rounded-lg border border-stone-800 bg-black p-6">
            <h4 className="mb-4 text-sm font-medium uppercase tracking-[0.2em] text-stone-300">
              Nouveau Témoignage
            </h4>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Nom du client *</Label>
                <Input
                  value={newTestimonial.client_name}
                  onChange={(e) => setNewTestimonial({ ...newTestimonial, client_name: e.target.value })}
                  className="mt-2 h-11 bg-white text-stone-900"
                  placeholder="Jean Dupont"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Note *</Label>
                <div className="mt-2 flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      onClick={() => setNewTestimonial({ ...newTestimonial, rating })}
                      className={`rounded-lg p-2 transition-colors ${
                        newTestimonial.rating >= rating
                          ? "bg-[#3b82f6] text-white"
                          : "bg-stone-900 text-stone-500 hover:bg-stone-800"
                      }`}
                    >
                      <Star className="h-5 w-5" fill={newTestimonial.rating >= rating ? "currentColor" : "none"} />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Texte de l&apos;avis *</Label>
                <Textarea
                  value={newTestimonial.text}
                  onChange={(e) => setNewTestimonial({ ...newTestimonial, text: e.target.value })}
                  className="mt-2 min-h-[100px] bg-white text-stone-900"
                  placeholder="Mon expérience avec Samuel a été incroyable..."
                />
              </div>

              <Button
                onClick={addTestimonial}
                disabled={saving || !newTestimonial.client_name || !newTestimonial.text}
                className="w-full rounded-full bg-[#3b82f6] py-5 text-white hover:bg-[#2563eb] disabled:opacity-50"
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? "Ajout en cours..." : "Ajouter le témoignage"}
              </Button>
            </div>
          </div>

          {/* Existing Testimonials */}
          <div>
            <h4 className="mb-4 text-sm font-medium uppercase tracking-[0.2em] text-stone-300">
              Témoignages Existants ({testimonials.length})
            </h4>
            {testimonials.length === 0 ? (
              <div className="rounded-lg border border-stone-800 p-8 text-center">
                <MessageSquare className="mx-auto h-12 w-12 text-stone-400" />
                <p className="mt-4 text-sm text-stone-500">Aucun témoignage pour le moment</p>
              </div>
            ) : (
              <div className="space-y-4">
                {testimonials.map((testimonial) => (
                  <div
                    key={testimonial.id}
                    className="rounded-lg border border-stone-800 bg-black p-5"
                  >
                    {editingTestimonial?.id === testimonial.id ? (
                      // Edit Mode
                      <div className="space-y-4">
                        <div>
                          <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Nom du client</Label>
                          <Input
                            value={editingTestimonial.client_name}
                            onChange={(e) => setEditingTestimonial({ ...editingTestimonial, client_name: e.target.value })}
                            className="mt-2 h-11 bg-white text-stone-900"
                          />
                        </div>

                        <div>
                          <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Note</Label>
                          <div className="mt-2 flex gap-2">
                            {[1, 2, 3, 4, 5].map((rating) => (
                              <button
                                key={rating}
                                onClick={() => setEditingTestimonial({ ...editingTestimonial, rating })}
                                className={`rounded-lg p-2 transition-colors ${
                                  editingTestimonial.rating >= rating
                                    ? "bg-[#3b82f6] text-white"
                                    : "bg-stone-900 text-stone-500 hover:bg-stone-800"
                                }`}
                              >
                                <Star className="h-5 w-5" fill={editingTestimonial.rating >= rating ? "currentColor" : "none"} />
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <Label className="text-xs uppercase tracking-[0.2em] text-stone-400">Texte de l&apos;avis</Label>
                          <Textarea
                            value={editingTestimonial.text}
                            onChange={(e) => setEditingTestimonial({ ...editingTestimonial, text: e.target.value })}
                            className="mt-2 min-h-[100px] bg-white text-stone-900"
                          />
                        </div>

                        <div className="flex gap-2">
                          <Button
                            onClick={() => updateTestimonial(testimonial.id)}
                            disabled={saving}
                            className="flex-1 rounded-full bg-[#3b82f6] py-3 text-white hover:bg-[#2563eb] disabled:opacity-50"
                          >
                            <Save className="mr-2 h-4 w-4" />
                            Enregistrer
                          </Button>
                          <Button
                            onClick={() => setEditingTestimonial(null)}
                            variant="outline"
                            className="flex-1 rounded-full border-stone-700 bg-transparent py-3 text-stone-400 hover:bg-stone-900 hover:text-white"
                          >
                            Annuler
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div>
                        <div className="mb-3 flex items-start justify-between">
                          <div>
                            <p className="font-medium text-white">{testimonial.client_name}</p>
                            <div className="mt-1 flex gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`h-4 w-4 ${
                                    star <= testimonial.rating ? "fill-[#3b82f6] text-[#3b82f6]" : "text-stone-700"
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => setEditingTestimonial(testimonial)}
                              variant="ghost"
                              size="sm"
                              className="text-stone-400 hover:bg-stone-900 hover:text-white"
                            >
                              Modifier
                            </Button>
                            <Button
                              onClick={() => deleteTestimonial(testimonial.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:bg-red-950 hover:text-red-300"
                            >
                              Supprimer
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm leading-relaxed text-stone-300">{testimonial.text}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
